import { useEffect, useRef, useState } from "react";
import { Download, Loader2 } from "lucide-react";

// Coup d'envoi : 10 novembre 2026, 09h00 — Kinshasa (UTC+1)
const EVENT_DATE = new Date("2026-11-10T09:00:00+01:00");

const FORMATS = [
  { id: "carre", label: "Carré 1080×1080 (post Instagram/Facebook)", w: 1080, h: 1080 },
  { id: "story", label: "Story 1080×1920 (Instagram/WhatsApp)", w: 1080, h: 1920 },
];

function joursRestants() {
  return Math.max(0, Math.ceil((EVENT_DATE.getTime() - Date.now()) / 86400000));
}

async function dessiner(canvas, format) {
  const { w, h } = format;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  const j = joursRestants();

  // Fond
  ctx.fillStyle = "#0A0A0C";
  ctx.fillRect(0, 0, w, h);
  // Liseré doré
  ctx.strokeStyle = "#C5A059";
  ctx.lineWidth = 6;
  ctx.strokeRect(w * 0.04, h * 0.03, w * 0.92, h * 0.94);
  // Bandeau or en haut
  ctx.fillStyle = "#C5A059";
  ctx.fillRect(w * 0.04, h * 0.03, w * 0.92, 6);

  // Logo
  const logo = new Image();
  logo.src = "/assets/logo-dark-sm.png";
  await new Promise((res) => { logo.onload = res; logo.onerror = res; });
  const logoH = h * 0.11;
  const logoW = logo.width && logo.height ? (logoH * logo.width) / logo.height : logoH;
  try { ctx.drawImage(logo, (w - logoW) / 2, h * 0.08, logoW, logoH); } catch { /* logo optionnel */ }

  const center = h * (format.id === "story" ? 0.42 : 0.44);
  const gold = "#C5A059";
  const grey = "#A1A1AA";

  ctx.textAlign = "center";
  ctx.fillStyle = gold;
  ctx.font = `600 ${Math.round(w * 0.032)}px Arial, sans-serif`;
  ctx.fillText("D É T E C T I O N   I N T E R N A T I O N A L E", w / 2, h * 0.24);

  // J-XX géant
  ctx.fillStyle = "#FFFFFF";
  ctx.font = `900 ${Math.round(w * 0.30)}px "Arial Black", Arial, sans-serif`;
  ctx.fillText(j > 0 ? `J-${j}` : "J-0", w / 2, center + w * 0.08);

  ctx.fillStyle = gold;
  ctx.font = `700 ${Math.round(w * 0.045)}px Arial, sans-serif`;
  ctx.fillText(j > 0 ? "AVANT LE COUP D'ENVOI" : "C'EST AUJOURD'HUI !", w / 2, center + w * 0.16);

  ctx.fillStyle = "#FFFFFF";
  ctx.font = `700 ${Math.round(w * 0.042)}px Arial, sans-serif`;
  ctx.fillText("KINSHASA — 10 → 21 NOVEMBRE 2026", w / 2, center + w * 0.24);

  ctx.fillStyle = grey;
  ctx.font = `${Math.round(w * 0.026)}px Arial, sans-serif`;
  ctx.fillText("U19 & U23 · Féminin & Masculin", w / 2, center + w * 0.30);
  ctx.fillText("1200 joueurs — 350 en phase finale · Hôtel Vey Plaza, Ngaliema", w / 2, center + w * 0.345);

  ctx.fillStyle = gold;
  ctx.font = `600 ${Math.round(w * 0.028)}px Arial, sans-serif`;
  ctx.fillText("dream2realityfa.com", w / 2, h * 0.94);
}

export default function AdminCompteRebours() {
  const [formatId, setFormatId] = useState("carre");
  const [ready, setReady] = useState(false);
  const canvasRef = useRef(null);
  const format = FORMATS.find((f) => f.id === formatId);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setReady(false);
      if (canvasRef.current) {
        await dessiner(canvasRef.current, format);
        if (!cancelled) setReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [format]);

  const telecharger = () => {
    canvasRef.current?.toBlob((blob) => {
      if (!blob) return;
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `D2R-compte-a-rebours-J${joursRestants()}-${format.id}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    }, "image/png");
  };

  return (
    <div className="border border-[#27272A] bg-[#121214] p-6" data-testid="admin-compte-rebours">
      <p className="text-[10px] uppercase tracking-widest text-[#C5A059] mb-2">Compte à rebours pour les réseaux sociaux</p>
      <p className="text-sm text-[#A1A1AA] mb-5 leading-relaxed">
        Image à jour générée automatiquement (<strong className="text-white">J-{joursRestants()}</strong> aujourd'hui) — téléchargez-la et postez-la sur Instagram, Facebook ou WhatsApp.
      </p>
      <div className="flex flex-wrap gap-3 mb-5">
        {FORMATS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFormatId(f.id)}
            data-testid={`countdown-format-${f.id}`}
            className={`px-4 py-2.5 text-[10px] uppercase tracking-wider border transition-colors ${formatId === f.id ? "bg-[#C5A059] text-black border-[#C5A059] font-semibold" : "border-[#27272A] text-[#A1A1AA] hover:border-[#C5A059] hover:text-[#C5A059]"}`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="flex flex-col sm:flex-row gap-6 items-start">
        <canvas
          ref={canvasRef}
          data-testid="countdown-canvas"
          className="border border-[#27272A] bg-[#0A0A0C] max-h-[420px] w-auto max-w-full"
        />
        <button
          type="button"
          onClick={telecharger}
          disabled={!ready}
          data-testid="countdown-download"
          className="inline-flex items-center gap-2 bg-[#C5A059] text-black px-6 py-3.5 text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#E5C07B] transition-colors disabled:opacity-60"
        >
          {ready ? <Download size={15} /> : <Loader2 size={15} className="animate-spin" />}
          Télécharger l'image (J-{joursRestants()})
        </button>
      </div>
    </div>
  );
}
